import React, { useEffect, useMemo, useState } from "react";

import { incomplete, useGetAdminSettings, useUpdateAdminSettings } from "utils";

import { AdminSection, Button, Col, Flex, Form, MainPage, Message, Modal, Option, Row, Select } from "components";

type SettingsData = {
  submissionStartDate?: string;
  submissionDueDate?: string;
  submissionEndDate?: string;
};

type DayMonthValue = {
  day: number | null;
  month: number | null;
};

// Parse MM/DD format from API
const parseDateString = (dateString: string | undefined): DayMonthValue | null => {
  if (!dateString) return null;
  const parts = dateString.split("/");
  if (parts.length === 2) {
    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return { month, day };
    }
  }
  return null;
};

// Format as MM/DD for API
const formatDateForAPI = (value: DayMonthValue | null | undefined): string | undefined => {
  if (!value || value.day === null || value.month === null) return undefined;
  return `${String(value.month).padStart(2, "0")}/${String(value.day).padStart(2, "0")}`;
};

export const Dates = () => {
  const { data: adminSettings, isLoading: isLoadingAdminSettings } = useGetAdminSettings();
  const { mutateAsync: updateAdminSettings, isPending: isPendingUpdateAdminSettings } = useUpdateAdminSettings();

  const [form] = Form.useForm();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submissionDateRows: { key: keyof SettingsData; label: string; description: string }[] = [
    {
      key: "submissionStartDate",
      label: "Submission Start Date",
      description: "Set the start date for submissions (day and month)",
    },
    {
      key: "submissionDueDate",
      label: "Submission Due Date",
      description: "Set the due date for submissions (day and month)",
    },
    {
      key: "submissionEndDate",
      label: "Submission End Date",
      description: "Set the end date for submissions (day and month)",
    },
  ];

  // Parse and memoize initial values from settings
  const initialValues = useMemo(() => {
    if (!adminSettings) {
      return {
        submissionStartDate: null,
        submissionDueDate: null,
        submissionEndDate: null,
      };
    }

    return {
      submissionStartDate: adminSettings.submissionStartDate ? parseDateString(adminSettings.submissionStartDate) : null,
      submissionDueDate: adminSettings.submissionDueDate ? parseDateString(adminSettings.submissionDueDate) : null,
      submissionEndDate: adminSettings.submissionEndDate ? parseDateString(adminSettings.submissionEndDate) : null,
    };
  }, [adminSettings]);

  // Initialize form when settings are loaded
  useEffect(() => {
    if (adminSettings && !isLoadingAdminSettings) {
      form.setFieldsValue({
        submissionStartDateMonth: initialValues.submissionStartDate?.month ?? null,
        submissionStartDateDay: initialValues.submissionStartDate?.day ?? null,
        submissionDueDateMonth: initialValues.submissionDueDate?.month ?? null,
        submissionDueDateDay: initialValues.submissionDueDate?.day ?? null,
        submissionEndDateMonth: initialValues.submissionEndDate?.month ?? null,
        submissionEndDateDay: initialValues.submissionEndDate?.day ?? null,
      });
    }
  }, [adminSettings, isLoadingAdminSettings, initialValues, form]);

  const handleSubmit = (values: {
    submissionStartDateMonth?: number | null;
    submissionStartDateDay?: number | null;
    submissionDueDateMonth?: number | null;
    submissionDueDateDay?: number | null;
    submissionEndDateMonth?: number | null;
    submissionEndDateDay?: number | null;
  }) => {
    setIsSubmitting(true);

    const submissionStartDate = formatDateForAPI({
      month: values.submissionStartDateMonth ?? null,
      day: values.submissionStartDateDay ?? null,
    });
    const submissionDueDate = formatDateForAPI({
      month: values.submissionDueDateMonth ?? null,
      day: values.submissionDueDateDay ?? null,
    });
    const submissionEndDate = formatDateForAPI({
      month: values.submissionEndDateMonth ?? null,
      day: values.submissionEndDateDay ?? null,
    });

    if (!submissionStartDate || !submissionDueDate || !submissionEndDate) {
      Message({
        icon: "error",
        content: "Please select a valid date for all fields",
      });
      return;
    }

    updateAdminSettings({
      submissionStartDate,
      submissionDueDate,
      submissionEndDate,
    })
      .then(() => {
        Message({
          icon: "tick",
          content: "Dates updated successfully",
        });
      })
      .catch(() => {
        Message({
          icon: "error",
          content: "Failed to update dates",
        });
      });

    setIsSubmitting(false);
    setShowConfirmationModal(false);
  };

  const dayOptions = Array.from({ length: 31 }, (_, i) => i + 1);
  const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);

  const isLoading = isLoadingAdminSettings || isPendingUpdateAdminSettings || isSubmitting;

  return (
    <MainPage loading={isLoading} pageName={"admin"}>
      <Row>
        <Col>
          <Form form={form} name="dates-form" onFinish={handleSubmit} onFinishFailed={() => incomplete()}>
            {submissionDateRows.map((row) => (
              <Row key={row.key}>
                <Col>
                  <AdminSection
                    headline={row.label}
                    description={row.description}
                    extra={
                      <Flex justify="space-between" gap="1rem">
                        <div style={{ width: "50%" }}>
                          {/* @ts-expect-error needs to be converted to ts */}
                          <Select
                            name={`${row.key}Month`}
                            label="Month"
                            initialValue={initialValues[row.key]?.month ?? null}
                            rules={[{ required: true, message: "Required" }]}
                          >
                            {monthOptions.map((month) => (
                              <Option key={month} value={month}>
                                {month}
                              </Option>
                            ))}
                          </Select>
                        </div>
                        <div style={{ width: "50%" }}>
                          {/* @ts-expect-error needs to be converted to ts */}
                          <Select
                            name={`${row.key}Day`}
                            label="Day"
                            initialValue={initialValues[row.key]?.day ?? null}
                            rules={[{ required: true, message: "Required" }]}
                          >
                            {dayOptions.map((day) => (
                              <Option key={day} value={day}>
                                {day}
                              </Option>
                            ))}
                          </Select>
                        </div>
                      </Flex>
                    }
                  />
                </Col>
              </Row>
            ))}
            <Row className="margin-top-200">
              <Col>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    disabled={isLoading}
                    loading={isLoading}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowConfirmationModal(true);
                    }}
                  >
                    Update dates
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      <Modal
        showModal={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onOk={() => form.submit()}
        title="Are you sure you want to update the submission dates?"
        okText="Confirm"
        cancelText="Cancel"
        loading={isLoading}
        destroyOnHidden
        content={
          <p>
            Updating the submission dates will affect <strong>ALL</strong> farms. This could have a significant impact on the data submission process. Please
            make sure you are aware of the implications before proceeding.
          </p>
        }
      />
    </MainPage>
  );
};
